import React, { useEffect, useMemo, useState } from 'react';
import { Banknote, Receipt, Scale } from 'lucide-react';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import Card from '../../../components/shared/Card/Card';
import Input from '../../../components/shared/Input/Input';
import Button from '../../../components/shared/Button/Button';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import LoadingSpinner from '../../../components/shared/LoadingSpinner/LoadingSpinner';
import { getDaySummary } from '../../../services/daySummary';
import type { DaySummaryResponse, DaySummaryRow } from '../../../types/api';

const isoDate = (d: Date) => d.toISOString().slice(0, 10);
const kes = (v: string | number) => `KES ${Number(v).toLocaleString()}`;

type DirectionFilter = 'all' | 'to_hub' | 'from_hub';

const DaySummary: React.FC = () => {
  const [date, setDate] = useState(isoDate(new Date()));
  const [data, setData] = useState<DaySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    getDaySummary(date)
      .then((d) => { if (active) setData(d); })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [date]);

  const hub = data?.hub ?? 'Nairobi';

  const rowMatches = (r: DaySummaryRow) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${r.vehicle_reg} ${r.route_name}`.toLowerCase().includes(q);
  };

  const groups = useMemo(
    () =>
      (data?.groups ?? [])
        .filter((g) => direction === 'all' || g.direction === direction)
        .map((g) => ({ ...g, rows: g.rows.filter(rowMatches) }))
        .filter((g) => g.rows.length > 0),
    [data, direction, query], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const chips: { key: DirectionFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'to_hub', label: `To ${hub}` },
    { key: 'from_hub', label: 'Upcountry' },
  ];

  return (
    <>
      <PageHeader
        title="Day summary"
        description="Per-vehicle revenue, expenses and net, split by direction."
        actions={
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date"
          />
        }
      />

      {data && (
        <StatStrip
          className="mb-6"
          stats={[
            { label: 'Revenue', value: kes(data.total_income), icon: <Banknote className="h-4 w-4" />, tone: 'income' },
            { label: 'Expenses', value: kes(data.total_expenses), icon: <Receipt className="h-4 w-4" />, tone: 'expense' },
            { label: 'Net', value: kes(data.net_income), icon: <Scale className="h-4 w-4" />, tone: 'income' },
          ]}
        />
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex gap-2">
          {chips.map((c) => (
            <Button
              key={c.key}
              size="sm"
              variant={direction === c.key ? 'primary' : 'secondary'}
              onClick={() => setDirection(c.key)}
            >
              {c.label}
            </Button>
          ))}
        </div>
        <div className="min-w-[220px] flex-1">
          <Input
            placeholder="Search plate or route…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search plate or route"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : failed ? (
        <Card><p className="text-sm text-red-600">Couldn't load the day summary.</p></Card>
      ) : groups.length === 0 ? (
        <Card><p className="text-sm text-slate-500">No revenue or expenses for this filter.</p></Card>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <Card
              key={g.direction}
              title={g.label}
              description={`Revenue ${kes(g.revenue)} · Expenses ${kes(g.expenses)} · Net ${kes(g.net)}`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Plate</th>
                      <th className="py-2 pr-4">Route</th>
                      <th className="py-2 pr-4 text-right">Revenue</th>
                      <th className="py-2 pr-4 text-right">Expenses</th>
                      <th className="py-2 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={`${r.vehicle_reg}-${r.route_name}`} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-medium text-slate-800">
                          {r.vehicle_reg}
                          {r.trip_count > 1 && (
                            <span className="ml-1 text-xs text-slate-400">×{r.trip_count}</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-slate-600">{r.route_name}</td>
                        <td className="py-2 pr-4 text-right">{kes(r.revenue)}</td>
                        <td className="py-2 pr-4 text-right">{kes(r.expenses)}</td>
                        <td className={`py-2 text-right font-semibold ${Number(r.net) < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                          {kes(r.net)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default DaySummary;
